<div class="apply-page">
  <div class="apply-hero">
    <div class="title">Søk om tilgang til arbeidsgiverportalen</div>
    <p>
      Fyll ut skjemaet nedenfor for å bestille tilgang til Arbeidsgiverportalen. Arbeidsgiver vil motta en e-post om
      forespørselen. Når vi har behandlet bestillingen vil det sendes bekreftelse per e-post.
    </p>
    <p>Felter merket med * må fylles ut.</p>
    <p class="helper-link link">
      Har du allerede fått tilgang, men kommer ikke inn?
      <a href="https://kontakt.fagforbundet.no" target="_blank" rel="noopener noreferrer">Ta kontakt med oss</a>
    </p>
  </div>

  <form class="apply-form" [formGroup]="applicationForm" (ngSubmit)="submit()">
    <section class="form-card requirements-card">
      @if (selfProfileLoaded && !hasRequiredProfileData) {
        <div class="status-message status-message--error link">
          <div class="status-title">Før du kan fortsette søknaden må dette være på plass i <a [routerLink]="'/user-profile'">Din profil</a>:</div>
          <ul class="requirement-list">
            <li [class.requirement-list-ok]="hasConfirmedEmail" [class.requirement-list-pending]="!hasConfirmedEmail">E-post må være bekreftet.</li>
            <li [class.requirement-list-ok]="hasSelfOrganizationNumber" [class.requirement-list-pending]="!hasSelfOrganizationNumber">
              Organisasjonsnr til egen arbeidsgiver må være fylt inn.
            </li>
          </ul>
        </div>
      }
    </section>

    <section class="form-card">
      <h2 class="sub-title">Opplysninger om arbeidsgiver</h2>
      <p>
        Oppgi organisasjonsnummeret til organisasjonen det skal rapporteres for. Organisasjonen må finnes som enhet
        eller underenhet i Enhetsregisteret.
      </p>
      <p class="helper-link link">
        <a href="https://brreg.no" class="ff-link brreg-link" target="_blank" rel="noopener noreferrer"
          >Du finner org.nr hos Brønnøysundsregisteret.</a
        >
      </p>

      <div class="field-row">
        <label for="orgnr" class="form-label">Organisasjonsnummer *</label>
        <input
          id="orgnr"
          type="text"
          class="form-control"
          placeholder="9 siffer"
          formControlName="organizationNumber"
        />
        @if (applicationForm.controls['organizationNumber'].hasError('pattern')) {
          <div class="text-danger mt-1">Org.nr må være 9 siffer</div>
        }
        @if (applicationForm.controls['organizationNumber'].hasError('notInBrReg')) {
          <div class="text-danger mt-1">Org.nr ikke funnet i Enhetsregisteret</div>
        }
        @if (applicationForm.controls['organizationNumber'].hasError('duplicate')) {
          <div class="text-danger mt-1">Du har allerede lagt til denne organisasjonen</div>
        }
      </div>

      <div class="field-row">
        <label for="organizationName" class="form-label">Organisasjonsnavn *</label>
        <div id="organizationName" class="readonly-value">
          {{ applicationForm.controls['organizationName'].value || 'Fylles automatisk når gyldig org.nr er lagt inn' }}
        </div>
      </div>

      @if (organizations.controls.length > 0) {
        <div class="selected-list-title">Arbeidsgivere du søker tilgang til</div>
      }
      @for (org of organizations.controls; track org; let i = $index) {
        <div class="selected-item">
          <span><strong>{{ org.value.navn }}</strong> ({{ org.value.organisasjonsnummer }})</span>
          <button type="button" class="btn-close" aria-label="Fjern organisasjon" (click)="removeOrganization(i)"></button>
        </div>
      }
    </section>

    <section class="form-card">
      <label class="sub-title">Er du regnskapsfører?</label>
      <div class="radio-row">
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            [value]="true"
            formControlName="isThirdParty"
            id="thirdPartyYes"
          />
          <label class="form-check-label" for="thirdPartyYes">Ja</label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            [value]="false"
            formControlName="isThirdParty"
            id="thirdPartyNo"
          />
          <label class="form-check-label" for="thirdPartyNo">Nei</label>
        </div>
      </div>
    </section>

    <section class="form-card terms-card">
      <div class="sub-title">Vilkår</div>
      <p>
        Jeg forplikter meg med dette til å behandle de personopplysningene jeg får kjennskap til etter
        Personopplysningslovens bestemmelser.
      </p>
      <p>
        Undertegnede er kjent med den lovpålagte taushetsplikt, herunder hvilke opplysninger som er omfattet av
        taushetsplikten og at taushetsbrudd kan medføre straffeansvar.
      </p>
      <p>Ved å fortsette godtar du vilkårene.</p>
      @if (submitError) {
        <div class="text-danger mt-2">{{ submitError }}</div>
      }
      <div class="terms-actions">
        <button type="submit" class="button primary" [disabled]="isSubmitDisabled">
          {{ submitting ? 'Sender...' : 'Send søknad' }}
        </button>
      </div>
    </section>
  </form>
</div>

@if (showApplicationSubmittedModal) {
  <div class="submission-modal-overlay" role="presentation">
    <div class="submission-modal" role="dialog" aria-modal="true" aria-labelledby="submissionModalTitle" (click)="$event.stopPropagation()">
      <button type="button" class="submission-modal-close" aria-label="Lukk" (click)="closeApplicationSubmittedModal()">&times;</button>
      <div id="submissionModalTitle" class="submission-modal-title">Din søknad er mottatt</div>
      <p>
        En e-post har blitt sendt til arbeidsgiveren det søkes om tilgang for. Når vi har behandlet din tilgang vil du
        få bekreftelse på e-post.
      </p>
      <p class="submission-modal-note-title">Merk:</p>
      <p>
        Du har ikke tilgang til organisasjonen før du får bekreftelse fra oss.
        Ta kontakt med oss hvis det er noe du lurer på.
      </p>
      <p class="submission-modal-signoff">Med hilsen</p>
      <img src="assets/images/svg/fagforbundet-logo-rgb.webp" alt="Fagforbundet" class="submission-modal-logo" />
      <button type="button" class="button primary submission-modal-button" (click)="closeApplicationSubmittedModal()">Lukk</button>
    </div>
  </div>
}

